import { Component, ViewChild, OnInit, ChangeDetectorRef } from '@angular/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { TaxonomyButtonComponent } from '../taxonomy-button/taxonomy-button.component';
import { PlaygroundService } from '../../../services/playground.service';
import { PlaygroundInputComponent } from './playground-input/playground-input.component';
import { PlaygroundResultsComponent } from './playground-results/playground-results.component';
import { CommonModule } from '@angular/common';
import { GetInsightsRequest } from '../taxonomy.model';
import { NotificationService } from '../../../services/notification.service';

@Component({
  selector: 'app-taxonomy-playground',
  standalone: true,
  imports: [
    CommonModule,
    MatFormFieldModule,
    MatSelectModule,
    TaxonomyButtonComponent,
    PlaygroundInputComponent,
    PlaygroundResultsComponent,
  ],
  templateUrl: './taxonomy-playground.component.html',
  styleUrl: './taxonomy-playground.component.scss',
})
export class TaxonomyPlaygroundComponent implements OnInit {
  @ViewChild(PlaygroundInputComponent) playgroundInput!: PlaygroundInputComponent;
  public failedCount: number = 0;
  private _insightGenerationCompleted: boolean = false;
  public showStopProcessingPopup: boolean = false;
  public showClearDataPopup: boolean = false;
  public showTaxonomyChangePopup: boolean = false;
  private currentTaxonomyInfo: { taxonomy_id: string; record_id: string } | null = null;

  // Getter for insightGenerationCompleted to ensure reactivity
  get insightGenerationCompleted(): boolean {
    return this._insightGenerationCompleted;
  }

  // Setter for insightGenerationCompleted with logging
  set insightGenerationCompleted(value: boolean) {
    this._insightGenerationCompleted = value;
    // Force change detection to ensure UI updates
    this.changeDetectorRef.detectChanges();
  }

  constructor(
    public playgroundService: PlaygroundService,
    private notificationService: NotificationService,
    private changeDetectorRef: ChangeDetectorRef
  ) {}

  ngOnInit() {
    // Load data from localStorage on component init
    this.loadFromLocalStorage();

    // Subscribe to taxonomy changes
    this.playgroundService.taxonomyChange$.subscribe(() => {
      this.handleTaxonomyChange();
    });
  }

  private handleTaxonomyChange() {
    const selectedTaxonomyStr = localStorage.getItem('selectedTaxonomy');
    if (!selectedTaxonomyStr) {
      return;
    }

    const selectedTaxonomy = JSON.parse(selectedTaxonomyStr);
    const newTaxonomyId = selectedTaxonomy.taxonomy_id;
    const newRecordId = selectedTaxonomy.selected_taxonomy_version?.record_id;

    if (!newTaxonomyId || !newRecordId) {
      return;
    }

    // If polling is in progress
    if (!this.insightGenerationCompleted) {
      this.showTaxonomyChangePopup = true;
      return;
    }

    // If data is processed, check if it matches current taxonomy
    const storedData = localStorage.getItem('playgroundData');
    if (storedData) {
      const parsedData = JSON.parse(storedData);
      if (parsedData.taxonomyInfo) {
        if (
          parsedData.taxonomyInfo.taxonomy_id !== newTaxonomyId ||
          parsedData.taxonomyInfo.record_id !== newRecordId
        ) {
          this.playgroundService.clearPlaygroundData();
          this.failedCount = 0;
          this.insightGenerationCompleted = false;
        } else {
          // console.log('Taxonomy/version matches, keeping data');
        }
      }
    } else {
      // console.log('No stored playground data found');
    }
  }

  private clearPlaygroundData() {
    this.playgroundService.clearPlaygroundData();
    this.failedCount = 0;
    this.insightGenerationCompleted = false;
  }

  stopProcessingForTaxonomyChange() {
    this.showTaxonomyChangePopup = false;
    this.stopProcessingAndNavigateBack();
  }

  cancelTaxonomyChange() {
    this.showTaxonomyChangePopup = false;
    // Revert taxonomy selection to previous value
    if (this.currentTaxonomyInfo) {
      const selectedTaxonomy = {
        taxonomy_id: this.currentTaxonomyInfo.taxonomy_id,
        selected_taxonomy_version: {
          record_id: this.currentTaxonomyInfo.record_id,
        },
      };
      localStorage.setItem('selectedTaxonomy', JSON.stringify(selectedTaxonomy));
    }
  }

  private loadFromLocalStorage() {
    const storedData = localStorage.getItem('playgroundData');

    if (storedData) {
      const parsedData = JSON.parse(storedData);

      // Validate taxonomy info before loading data
      const selectedTaxonomyStr = localStorage.getItem('selectedTaxonomy');
      if (selectedTaxonomyStr) {
        const selectedTaxonomy = JSON.parse(selectedTaxonomyStr);
        const currentTaxonomyId = selectedTaxonomy.taxonomy_id;
        const currentRecordId = selectedTaxonomy.selected_taxonomy_version?.record_id;

        if (
          parsedData.taxonomyInfo &&
          parsedData.taxonomyInfo.taxonomy_id === currentTaxonomyId &&
          parsedData.taxonomyInfo.record_id === currentRecordId
        ) {
          this.playgroundService.updateProcessedData(parsedData.data);
          if (parsedData.status) {
            this.insightGenerationCompleted = parsedData.status.insightGenerationCompleted;
            this.failedCount = parsedData.status.failedCount;
            this.playgroundService.updateStatusCounts(parsedData.status.statusCounts);
          }
        } else {
          this.clearPlaygroundData();
        }
      }
    } else {
      // console.log('No stored playground data found');
    }
  }

  private saveToLocalStorage(data: any, status: any, taxonomyInfo?: { taxonomy_id: string; record_id: string }) {
    const storageData = {
      data,
      status,
      taxonomyInfo,
    };
    localStorage.setItem('playgroundData', JSON.stringify(storageData));
  }

  private clearLocalStorage() {
    localStorage.removeItem('playgroundData');
    localStorage.removeItem('playgroundStatus');
  }

  handleTestResultNavigation() {
    if (!this.insightGenerationCompleted) {
      this.showStopProcessingPopup = true;
    } else {
      this.showClearDataPopup = true;
    }
  }

  stopProcessingAndNavigateBack() {
    this.playgroundService.cancelCurrentPolling();
    this.showStopProcessingPopup = false;
    this.clearLocalStorage();
    this.playgroundService.playgroundStage = 'input';
    this.playgroundService.updateProcessedData([]);
    this.playgroundService.updateStatusCounts({ todo: 0, in_progress: 0, done: 0, failed: 0 });
    this.failedCount = 0;
    this.insightGenerationCompleted = false;

    if (this.playgroundInput) {
      this.playgroundInput.clearUploadState();
    }
  }

  clearDataAndNavigateBack() {
    this.showClearDataPopup = false;
    this.clearLocalStorage();
    this.playgroundService.playgroundStage = 'input';
    this.playgroundService.updateProcessedData([]);
    this.playgroundService.updateStatusCounts({ todo: 0, in_progress: 0, done: 0, failed: 0 });
    this.failedCount = 0;
    this.insightGenerationCompleted = false;
  }

  cancelStopProcessing() {
    this.showStopProcessingPopup = false;
  }

  cancelClearData() {
    this.showClearDataPopup = false;
  }

  async handleRunTest() {
    if (this.playgroundInput) {
      try {
        // Clear existing data and localStorage when starting new test
        this.clearPlaygroundData();

        // Cancel any existing polling before starting a new test
        this.playgroundService.cancelCurrentPolling();

        const processedData = await this.playgroundInput.processFiles();

        // Get selected value from localStorage
        const selectedTaxonomyStr = localStorage.getItem('selectedTaxonomy');
        if (!selectedTaxonomyStr) {
          console.error('No selected taxonomy found in localStorage');
          return;
        }

        const selectedTaxonomy = JSON.parse(selectedTaxonomyStr);
        const taxonomyId = selectedTaxonomy.taxonomy_id;
        const recordId = selectedTaxonomy.selected_taxonomy_version?.record_id;

        if (!taxonomyId || !recordId) {
          console.error('Missing taxonomy_id or record_id in selectedTaxonomy:', selectedTaxonomy);
          return;
        }

        // Store current taxonomy info
        this.currentTaxonomyInfo = {
          taxonomy_id: taxonomyId,
          record_id: recordId,
        };

        // Store taxonomy info for later use
        const taxonomyInfo = {
          taxonomy_id: taxonomyId,
          record_id: recordId,
        };

        // Determine input type based on user selection
        const inputType = this.playgroundService.selectedInputType === 'contactId' ? 'ContactId' : 'AudioTranscript';

        // Prepare payload for generateInsights API
        const payload = {
          taxonomy_id: taxonomyId,
          record_id: recordId,
          input: {
            contact_id_list: processedData[0], // Take the first array of contact IDs
          },
          input_type: inputType,
        };

        // Store the processed data in the service for later use
        this.playgroundService.processedData = processedData;

        // Show processing animation by changing to results view with inProgress status
        this.playgroundService.playgroundStage = 'results';
        this.playgroundService.playgroundResultStatus = 'inProgress';

        // Make API call to generateInsights
        this.playgroundService.generateInsights(payload).subscribe({
          next: (response: any) => {
            if (response.success && response.data?.batch_id) {
              this.getInsights(response.data.batch_id, taxonomyInfo);
            } else {
              console.error('No batch_id received in response or request was not successful');
              this.playgroundService.playgroundResultStatus = 'error';
              this.notificationService.error('Error', 'Failed to generate insights');
            }
          },
          error: (error) => {
            console.error('Error running test:', error);
            this.playgroundService.playgroundResultStatus = 'error';
            this.notificationService.error('Error', 'Failed to generate insights');
          },
        });
      } catch (error) {
        console.error('Error processing files:', error);
        this.playgroundService.playgroundResultStatus = 'error';
        this.notificationService.error('Error', 'Failed to process files');
      }
    }
  }

  getInsights(batchId: string, taxonomyInfo: { taxonomy_id: string; record_id: string }) {
    let pollCount = 0;
    const maxPolls = 75;
    const pollInterval = 30000;

    // Initialize playground data in localStorage when polling starts
    const initialPlaygroundData = {
      data: [],
      status: {
        insightGenerationCompleted: false,
        failedCount: 0,
        statusCounts: { todo: 0, in_progress: 0, done: 0, failed: 0 },
      },
      taxonomyInfo: taxonomyInfo,
    };
    this.saveToLocalStorage(
      [],
      {
        insightGenerationCompleted: false,
        failedCount: 0,
        statusCounts: { todo: 0, in_progress: 0, done: 0, failed: 0 },
      },
      taxonomyInfo
    );

    const poll = () => {
      if (pollCount >= maxPolls) {
        this.playgroundService.playgroundResultStatus = 'error';
        this.notificationService.error('Error', 'Maximum polling attempts reached');
        return;
      }

      const payload: GetInsightsRequest = { batch_id: batchId };

      this.playgroundService.getInsights(payload).subscribe({
        next: (response: any) => {
          if (response.success) {
            // Immediately update status counts from API response
            if (response.data?.status_counts) {
              this.playgroundService.updateStatusCounts(response.data.status_counts);
              this.failedCount = response.data.status_counts.failed || 0;
            }

            // Check if insight generation is completed
            const isCompleted = response.data?.insight_generation_completed === true;

            // Immediately update completion status
            if (response.data?.insight_generation_completed !== undefined) {
              this.insightGenerationCompleted = response.data.insight_generation_completed;

              // Save to localStorage immediately when insight generation is completed
              if (this.insightGenerationCompleted) {
                const currentData = this.playgroundService.processedData;
                this.saveToLocalStorage(
                  currentData,
                  {
                    insightGenerationCompleted: true,
                    failedCount: this.failedCount,
                    statusCounts: response.data.status_counts,
                  },
                  taxonomyInfo
                );
              }
            }

            // Immediately update table with any new data received
            if (response.data?.done_items) {
              const transformedData = response.data.done_items.map((item: any) => ({
                contactId: item.contactid,
                primaryTopic: item.insights.PrimaryTopic,
                intent: item.insights.Intent,
                topics: item.insights.TopicsClustered?.split('|') || [],
                sentimentAtStart: item.insights.CustomerSentimentAtStart,
                sentimentAtEnd: item.insights.CustomerSentimentAtEnd,
                journeyMoment: item.insights.JourneyMoment,
                painPointCluster: item.insights.CustomerPainPointClustered?.split('|') || [],
                painPoint: item.insights.CustomerPainPoint,
                rootCauseCluster: item.insights.RootCauseClustered?.split('|') || [],
                rootCause: item.insights.RootCause,
                topicCluster: item.insights.Topics?.split('|') || [],
                topic: item.insights.PrimaryTopic,
                summary: item.insights.Summary,
                callerType: item.insights.CallerType,
                trigger: item.insights.Trigger,
                triggerClustered: item.insights.TriggerClustered?.split('|') || [],
                outcome: item.insights.Outcome,
                outcomeClustered: item.insights.OutcomeClustered?.split('|') || [],
                callResolution: item.insights.CallResolution,
              }));

              // Save to localStorage for every API call
              this.saveToLocalStorage(
                transformedData,
                {
                  insightGenerationCompleted: this.insightGenerationCompleted,
                  failedCount: this.failedCount,
                  statusCounts: response.data.status_counts,
                },
                taxonomyInfo
              );

              // Only update UI when we have items
              if (response.data.done_items.length > 0) {
                // Immediately update processed data
                this.playgroundService.updateProcessedData(transformedData);
                this.playgroundService.playgroundResultStatus = 'resultPresent';

                if (isCompleted) {
                  this.notificationService.success('Success', 'Taxonomy Test Results Available!');
                }
              }
            }

            // Handle completion logic
            if (isCompleted) {
              // Ensure the completion state is properly set
              this.handleInsightGenerationCompleted();

              // If no items were received, show no data state
              if (!response.data?.done_items || response.data.done_items.length === 0) {
                this.playgroundService.playgroundResultStatus = 'noData';
                this.notificationService.error('No Data', 'No results available');
              }

              // Stop polling - don't continue
              return;
            } else {
              // Continue polling only if not completed
              pollCount++;
              this.playgroundService.setPollingTimeout(poll, pollInterval);
            }
          } else {
            this.playgroundService.playgroundResultStatus = 'error';
            this.notificationService.error('Error', 'Failed to get insights');
          }
        },
        error: (error) => {
          console.error('Error polling getInsights:', error);
          this.playgroundService.playgroundResultStatus = 'error';
          this.notificationService.error('Error', 'Failed to get insights');
        },
      });
    };

    poll();
  }

  handleInsightGenerationCompleted() {
    // Ensure the completion state is set to true
    this.insightGenerationCompleted = true;

    // Use setTimeout to ensure UI update happens in next tick
    setTimeout(() => {
      // Force change detection to update the UI immediately
      this.changeDetectorRef.detectChanges();

      // Show success notification
      this.notificationService.success('Success', 'Taxonomy Test Results Available!');
    }, 0);
  }
}
